import React, {useMemo, useState} from "react";
import "../styles/admin_page.css";
import DatePicker from "react-datepicker";
import {AdminRequest} from "../../models/AdminRequest";
import {Orders} from "../../models/Orders";
import {BASE_URLS} from "../../config/urls";
import {apiFetch} from "../../utils/api";
import {AdminOrdersChangeModal} from "./AdminOrdersChangeModal";
import {ExportCSV} from "./ExportCSV";
import {Link} from "react-router-dom";
import SendCityAndStore from "../forms/SendCityAndStore";

type Mode = "view" | "manage";

function shiftDate(d: Date | null): Date | null {
    if (!d) return null;
    const copy = new Date(d.getTime());
    copy.setDate(copy.getDate() + 1);
    return copy;
}

export default function AdminOrdersShared({mode}: { mode: Mode }) {
    const isManage = mode === "manage";

    const [store, setStore] = useState("Wildberries");
    const [sendCity, setSendCity] = useState("Казань");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [entity, setEntity] = useState("");

    const [startDepartureDate, setStartDepartureDate] = useState<null | Date>(null);
    const [endDepartureDate, setEndDepartureDate] = useState<null | Date>(null);
    const [startOrderDate, setStartOrderDate] = useState<null | Date>(null);
    const [endOrderDate, setEndOrderDate] = useState<null | Date>(null);
    const [acceptanceDate, setAcceptanceDate] = useState<null | Date>(null);

    const [status, setStatus] = useState(""); // в view можно оставить, не мешает
    const [sortBy, setSortBy] = useState("");

    const [orders, setOrders] = useState<Array<Orders>>([]);
    const [isLoading, setIsLoading] = useState(false);

    // только для manage
    const [ordersIndexes, setOrdersIndexes] = useState<Array<number>>([]);
    const [isPopupVisible, setIsPopupVisible] = useState(false);

    // ===== handlers =====

    function handleStore(nextStore: string) {
        setStore(nextStore);
        if (nextStore === "") setSendCity("");
    }

    function handleSendCity(city: string) {
        setSendCity(city);
    }

    function handlePhone(e: React.ChangeEvent<HTMLInputElement>) {
        setPhoneNumber(e.target.value);
    }

    function handleEntity(e: React.ChangeEvent<HTMLInputElement>) {
        setEntity(e.target.value);
    }

    function changeStatus(e: React.ChangeEvent<HTMLSelectElement>) {
        setStatus(e.target.value);
    }

    function changeSortBy(e: React.ChangeEvent<HTMLSelectElement>) {
        setSortBy(e.target.value);
    }

    function fetchAdmin() {
        const body = JSON.stringify(
            new AdminRequest(
                store,
                sendCity,
                phoneNumber,
                entity,
                shiftDate(startDepartureDate),
                shiftDate(endDepartureDate),
                shiftDate(startOrderDate),
                shiftDate(endOrderDate),
                shiftDate(acceptanceDate),
                status,
                sortBy
            )
        );

        setIsLoading(true);

        apiFetch(`${BASE_URLS.api}/admin`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body,
        })
            .then((resp) => resp.json())
            .then((data) => {
                setOrders(Array.isArray(data) ? data : []);
                setOrdersIndexes([]); // чтобы после нового поиска не висели старые выделения
            })
            .catch((e) => {
                console.error(e);
                setOrders([]);
                setOrdersIndexes([]);
            })
            .finally(() => setIsLoading(false));
    }

    function handleForm(event: React.FormEvent) {
        event.preventDefault();
        fetchAdmin();
    }

    // ===== manage-only actions =====
    function setPopupTrue() {
        setIsPopupVisible(true);
        document.body.style.overflow = "hidden";
    }

    function setPopupFalse() {
        setIsPopupVisible(false);
        document.body.style.overflow = "auto";
        fetchAdmin();
    }

    function handleCheckbox(index: number, e: React.ChangeEvent<HTMLInputElement>): void {
        if (e.target.checked) {
            setOrdersIndexes((prev) => [...prev, index]);
        } else {
            setOrdersIndexes((prev) => prev.filter((i) => i !== index));
        }
    }

    function setAllIndexes() {
        const allIndexes: number[] = [];
        orders.forEach((_, index) => allIndexes.push(index));
        setOrdersIndexes(allIndexes);
    }

    function setNoneIndexes() {
        setOrdersIndexes([]);
    }

    const selectedOrders = useMemo(() => ordersIndexes.map((ind) => orders[ind]).filter(Boolean), [ordersIndexes, orders]);

    return (
        <>
            {isManage && (
                <AdminOrdersChangeModal
                    isOpen={isPopupVisible}
                    orders={selectedOrders}
                    onClose={setPopupFalse}
                    onSaved={fetchAdmin} // после сохранения — обновим таблицу
                />
            )}

            {/* Карточка с фильтрами */}
            <section className="admin-card admin-card-filters">
                <h1 className="admin-heading">{isManage ? "Управление заказами" : "Просмотр заказов"}</h1>
                <p className="admin-subtitle">
                    Отфильтруйте заказы по городу, складу, датам и статусу, чтобы быстро найти нужные заявки.
                </p>

                <form className="admin_form" onSubmit={handleForm}>
                    <SendCityAndStore
                        handleSendCity={handleSendCity}
                        handleStore={handleStore}
                        location="admin"
                    />

                    <div className="admin-field">
                        <div className="admin_title">Номер телефона</div>
                        <input type="text" onInput={handlePhone} className="admin-input" placeholder="+7..."/>
                    </div>

                    <div className="admin-field">
                        <div className="admin_title">Имя</div>
                        <input type="text" onInput={handleEntity} className="admin-input" placeholder="Имя клиента"/>
                    </div>

                    <div className="admin-filter-grid">
                        <div className="admin-field">
                            <div className="admin_title">От даты доставки</div>
                            <DatePicker
                                selected={startDepartureDate}
                                onChange={(date: Date | null) => setStartDepartureDate(date)}
                                calendarStartDay={1}
                                dateFormat={"dd.MM.yyyy"}
                            />
                        </div>

                        <div className="admin-field">
                            <div className="admin_title">До даты доставки</div>
                            <DatePicker
                                selected={endDepartureDate}
                                onChange={(date: Date | null) => setEndDepartureDate(date)}
                                calendarStartDay={1}
                                dateFormat={"dd.MM.yyyy"}
                            />
                        </div>

                        <div className="admin-field">
                            <div className="admin_title">От даты заказа</div>
                            <DatePicker
                                selected={startOrderDate}
                                onChange={(date: Date | null) => setStartOrderDate(date)}
                                calendarStartDay={1}
                                dateFormat={"dd.MM.yyyy"}
                            />
                        </div>

                        <div className="admin-field">
                            <div className="admin_title">До даты заказа</div>
                            <DatePicker
                                selected={endOrderDate}
                                onChange={(date: Date | null) => setEndOrderDate(date)}
                                calendarStartDay={1}
                                dateFormat={"dd.MM.yyyy"}
                            />
                        </div>

                        <div className="admin-field">
                            <div className="admin_title">Дата приема</div>
                            <DatePicker
                                selected={acceptanceDate}
                                onChange={(date: Date | null) => setAcceptanceDate(date)}
                                calendarStartDay={1}
                                dateFormat={"dd.MM.yyyy"}
                            />
                        </div>
                    </div>

                    {/* Статус показываем только в manage (если в view он реально не нужен) */}
                    {isManage && (
                        <div className="admin-field">
                            <div className="admin_title">Статус</div>
                            <select value={status} onChange={changeStatus} className="select_admin admin-select">
                                <option value="">Без фильтра</option>
                                <option value="Ожидает на ФФ">Ожидает на ФФ</option>
                                <option value="Ожидает оплаты">Ожидает оплаты</option>
                                <option value="Доставка на склад">Доставка на склад</option>
                                <option value="Доставлен">Доставлен</option>
                            </select>
                        </div>
                    )}

                    <div className="admin-field">
                        <div className="admin_title">Сортировать по</div>
                        <select value={sortBy} onChange={changeSortBy} className="select_admin admin-select">
                            <option value="">Без сортировки</option>
                            <option value="departure_city">Город отправки</option>
                            <option value="store">Склад</option>
                            <option value="send_city">Город назначения</option>
                            <option value="phone_number">Номер телефона</option>
                            <option value="departure_date">Дата доставки</option>
                            <option value="order_date">Дата заказа</option>
                            <option value="status">Статус</option>
                        </select>
                    </div>

                    <button type="submit" className="admin-button-primary admin-button-primary--full"
                            disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <span className="admin-spinner"/> Загрузка...
                            </>
                        ) : (
                            "Найти заказы"
                        )}
                    </button>
                </form>
            </section>

            {/* Карточка действий + таблица */}
            <section className="admin-card">
                {/* actions — только manage */}
                {isManage && (
                    <div className="admin-actions-header">
                        <div className="admin-actions-group">
                            <button type="button" className="admin-button-secondary" onClick={setAllIndexes}
                                    disabled={isLoading}>
                                Выбрать все заказы
                            </button>
                            <button
                                type="button"
                                className="admin-button-secondary admin-button-secondary--ghost"
                                onClick={setNoneIndexes}
                                disabled={isLoading}
                            >
                                Снять выделение
                            </button>
                        </div>

                        <div className="admin-actions-group" style={{marginRight: 50}}>
                            <button
                                type="button"
                                className="admin-button-secondary admin-button-secondary--ghost"
                                onClick={setPopupTrue}
                                disabled={ordersIndexes.length === 0 || isLoading}
                            >
                                Изменить выбранные заказы
                            </button>

                            {/* Изменить 1 заказ (ссылка) */}
                            {ordersIndexes.length === 1 && !isLoading && (() => {
                                const selectedOrder = orders[ordersIndexes[0]];
                                const id = (selectedOrder as any)?.id;
                                if (!id) return null;

                                return (
                                    <Link
                                        to={`/admin/orders/${id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="admin-button-secondary"
                                        style={{marginLeft: 8,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textDecoration: "none",
                                        }}
                                    >
                                        Изменить 1 заказ
                                    </Link>
                                );
                            })()}

                            <ExportCSV csvData={orders} fileName="orders"/>
                        </div>
                    </div>
                )}

                <div className="admin-table-wrapper">
                    <table className="admin_table admin-orders-table">
                        <thead>
                        <tr>
                            {isManage && <th style={{width: "3%"}}>Галочка</th>}

                            <th style={{width: "10%"}}>
                                Дата
                                <br/> заказа
                            </th>

                            <th style={{width: "10%"}}>
                                Дата
                                <br/> поставки
                            </th>

                            <th style={{width: "10%"}}>Город назначения</th>
                            <th style={{width: "10%"}}>Склад</th>
                            <th style={{width: "10%"}}>Номер телефона</th>
                            <th style={{width: "10%"}}>Имя</th>

                            {/* расширенные поля только в manage */}
                            {isManage && <th style={{width: "5%"}}>ИНН</th>}

                            <th style={{width: "10%"}}>Тип поставки</th>
                            <th style={{width: "5%"}}>Объем</th>

                            {isManage && <th style={{width: "5%"}}>Цена</th>}

                            <th style={{width: "5%"}}>Забор</th>

                            {isManage && (
                                <>
                                    <th style={{width: "5%"}}>Где оплата</th>
                                    <th style={{width: "5%"}}>Оплачен</th>
                                    <th style={{width: "10%"}}>Статус</th>
                                    <th style={{width: "5%"}}>Изменяемый</th>
                                </>
                            )}
                        </tr>
                        </thead>

                        <tbody>
                        {isLoading && (
                            <tr>
                                <td colSpan={isManage ? 17 : 10} className="admin-table-loading">
                                    <span className="admin-spinner"/>
                                    Загрузка заказов...
                                </td>
                            </tr>
                        )}

                        {!isLoading &&
                            orders.map((order, index) => (
                                <tr key={index}>
                                    {isManage && (
                                        <td>
                                            <input
                                                type="checkbox"
                                                onChange={(e) => handleCheckbox(index, e)}
                                                checked={ordersIndexes.includes(index)}
                                            />
                                        </td>
                                    )}

                                    <td>
                                        {order.orderDateTime
                                            ? new Date(order.orderDateTime).toLocaleString("ru-RU", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })
                                            : ""}
                                    </td>

                                    <td>{order.departureDate !== undefined ? order.departureDate.toString() : ""}</td>
                                    <td>{order.sendCity}</td>
                                    <td>{order.store}</td>
                                    <td>{order.phoneNumber}</td>
                                    <td>{order.entity}</td>

                                    {isManage && <td>{(order as any).inn}</td>}

                                    <td>{order.supplyType}</td>
                                    <td>{order.volume}</td>

                                    {isManage && <td>{(order as any).price}</td>}

                                    <td>{order.willTaken ? "Да" : "Нет"}</td>

                                    {isManage && (
                                        <>
                                            <td>{(order as any).paymentSite ? "Онлайн" : "Оффлайн"}</td>
                                            <td>{(order as any).paymentStatus ? "Да" : "Нет"}</td>
                                            <td>{order.status}</td>
                                            <td>{(order as any).changeable ? "Да" : "Нет"}</td>
                                        </>
                                    )}
                                </tr>
                            ))}

                        {!isLoading && orders.length === 0 && (
                            <tr>
                                <td colSpan={isManage ? 17 : 10} className="admin-table-empty">
                                    Для отображения данных задайте фильтры и нажмите «Найти заказы».
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </section>
        </>
    );
}
